export const M4_UNIT_16 = {
  id: "m4-u16-superlatives",
  title: "Unidad 16: Talking About Extremes / Hablando de Extremos",
  topic: "Talk about THE best, THE biggest, THE most amazing! Learn superlative adjectives to describe extremes. Compare multiple things and express strong opinions about what's #1!",
  iconType: "trophy",
  grammarImage: "/grammar/grammar-m4-u16.jpg",
  grammarText: "SUPERLATIVE ADJECTIVES: THE + adjective + -EST (the biggest, the coldest, the fastest) for short adjectives. THE MOST + adjective (the most expensive, the most beautiful) for long adjectives. ALWAYS use THE before superlative! Spelling rules: big→the biggest (double), easy→the easiest (y→i), nice→the nicest (add -ST). IRREGULAR: good→the best, bad→the worst, far→the farthest/furthest. Use with present simple: 'Toronto IS the biggest city' (fact), 'I think summer IS the best' (opinion).",
  
  sets: [
    // SET 1: Introduction to Superlatives
    {
      id: "m4-u16-set1",
      title: "Set 1: Introduction to Superlatives",
      questions: [
        {
          type: "match",
          q: "Match comparative to superlative:",
          pairs: [
            { left: "bigger (2 things)", right: "THE biggest (3+ things)" },
            { left: "colder (2 things)", right: "THE coldest (3+ things)" },
            { left: "faster (2 things)", right: "THE fastest (3+ things)" }
          ],
          a: "completed",
          tip: "Pattern change! Comparative (2) → Superlative (3+). Always add THE!"
        },
        {
          type: "mc",
          q: "Comparatives compare 2 things. Superlatives describe:",
          options: [
            "The most extreme out of 3+ things",
            "Only 2 things",
            "Nothing"
          ],
          a: "The most extreme out of 3+ things",
          tip: "SUPERLATIVE = EXTREME! 🏆 Which is #1? THE biggest, THE best, THE most!"
        },
        {
          type: "input",
          q: "Complete: 'Toronto is ___ biggest city in Canada.' (always use this word before superlative)",
          a: "the"
        },
        {
          type: "order",
          q: "Ordena: 'Toronto es la ciudad más grande de Canadá'",
          words: ["Toronto", "is", "the", "biggest", "city", "in", "Canada."],
          a: "Toronto is the biggest city in Canada."
        },
        {
          type: "mc",
          q: "Rule: Superlatives ALWAYS use:",
          options: [
            "THE before the adjective",
            "THAN after the adjective",
            "Nothing special"
          ],
          a: "THE before the adjective",
          successtip: "✓ ALWAYS THE! Critical rule! 'THE biggest' ✓ NOT 'biggest' ❌. This is the #1 superlative rule!"
        },
        {
          type: "input",
          q: "Complete: 'Mount Everest is ___ highest mountain in the world.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "'Toronto, Montreal, Vancouver. Toronto is ___ city.' (más grande de las tres)",
          options: ["the biggest", "bigger", "big"],
          a: "the biggest",
          tip: "THE BIGGEST! Three cities = superlative! Comparing 3+ things = use superlative!"
        },
        {
          type: "order",
          q: "Ordena: 'El verano es la estación más calurosa'",
          words: ["Summer", "is", "the", "hottest", "season."],
          a: "Summer is the hottest season."
        },
        {
          type: "input",
          q: "Complete: 'Winter is ___ coldest season.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "Difference between comparative and superlative:",
          options: [
            "Comparative = 2 things + THAN. Superlative = 3+ things + THE",
            "They are the same",
            "No difference"
          ],
          a: "Comparative = 2 things + THAN. Superlative = 3+ things + THE",
          tip: "KEY DIFFERENCE! Comparative: 'A is bigger THAN B'. Superlative: 'A is THE biggest'!"
        }
      ]
    },

    // SET 2: Spelling Rules for Superlatives
    {
      id: "m4-u16-set2",
      title: "Set 2: Spelling Rules (-EST)",
      questions: [
        {
          type: "match",
          q: "Match adjective to superlative spelling:",
          pairs: [
            { left: "big → the biggest", right: "Double final consonant" },
            { left: "nice → the nicest", right: "Ends in E, just add -ST" },
            { left: "easy → the easiest", right: "Y → I, then add -EST" }
          ],
          a: "completed",
          tip: "Same spelling rules as comparatives! But use -EST instead of -ER!"
        },
        {
          type: "mc",
          q: "'Big' → superlative spelling:",
          options: ["the biggest (double G)", "the bigest", "the most big"],
          a: "the biggest (double G)",
          tip: "DOUBLE IT! Big → THE biggest. Hot → THE hottest. Sad → THE saddest!"
        },
        {
          type: "input",
          q: "Complete: 'Toronto is ___ biggest city in Canada.' (the)",
          a: "the"
        },
        {
          type: "order",
          q: "Ordena: 'Hoy es el día más caluroso del año'",
          words: ["Today", "is", "the", "hottest", "day", "of", "the", "year."],
          a: "Today is the hottest day of the year."
        },
        {
          type: "mc",
          q: "'Nice' ends in E. Superlative:",
          options: ["the nicest (just add -ST)", "the niceest", "the most nice"],
          a: "the nicest (just add -ST)",
          successtip: "✓ JUST ADD -ST! Nice → the nicest! Already has E, don't add -EST, just -ST!"
        },
        {
          type: "input",
          q: "Complete: 'This is ___ nicest hotel in the city.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "'Easy' ends in Y. Superlative:",
          options: ["the easiest (Y → I)", "the easyest", "the most easy"],
          a: "the easiest (Y → I)",
          tip: "Y → I! Easy → the easiest. Happy → the happiest. Busy → the busiest!"
        },
        {
          type: "order",
          q: "Ordena: 'Este es el examen más fácil del curso'",
          words: ["This", "is", "the", "easiest", "test", "in", "the", "course."],
          a: "This is the easiest test in the course."
        },
        {
          type: "input",
          q: "Complete: 'She is ___ happiest person I know.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "Pattern for short adjectives (1-2 syllables):",
          options: [
            "THE + adjective + -EST",
            "THE MOST + adjective",
            "Just adjective + -EST"
          ],
          a: "THE + adjective + -EST",
          successtip: "✓ PATTERN! THE + short adjective + -EST! 'The biggest, the coldest, the fastest'!"
        }
      ]
    },

    // SET 3: THE MOST + Long Adjectives
    {
      id: "m4-u16-set3",
      title: "Set 3: THE MOST + Long Adjectives",
      questions: [
        {
          type: "match",
          q: "Match long adjectives to superlative form:",
          pairs: [
            { left: "expensive", right: "the most expensive (NOT expensivest!)" },
            { left: "beautiful", right: "the most beautiful" },
            { left: "interesting", right: "the most interesting" }
          ],
          a: "completed",
          tip: "Long adjectives (3+ syllables) use THE MOST! Never add -EST!"
        },
        {
          type: "mc",
          q: "'Expensive' has 3 syllables. Superlative:",
          options: ["the most expensive", "the expensivest", "the expensiver"],
          a: "the most expensive",
          tip: "THE MOST EXPENSIVE! Long word = use THE MOST! Never add -EST to long words!"
        },
        {
          type: "input",
          q: "Complete: 'This is ___ most expensive hotel in the city.' (the)",
          a: "the"
        },
        {
          type: "order",
          q: "Ordena: 'Esta es la ciudad más hermosa del mundo'",
          words: ["This", "is", "the", "most", "beautiful", "city", "in", "the", "world."],
          a: "This is the most beautiful city in the world."
        },
        {
          type: "mc",
          q: "When to use THE MOST + adjective:",
          options: [
            "Long adjectives (usually 3+ syllables)",
            "All adjectives",
            "Only short adjectives"
          ],
          a: "Long adjectives (usually 3+ syllables)",
          successtip: "✓ LONG WORDS! Rule! 📏 3+ syllables = THE MOST! Beautiful, expensive, interesting!"
        },
        {
          type: "input",
          q: "Complete: 'This is ___ most interesting museum in town.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "'This vacation is ___.' (most + exciting - 3 syllables)",
          options: ["the most exciting", "the excitingest", "the excitinger"],
          a: "the most exciting",
          tip: "THE MOST EXCITING! 3 syllables = use THE MOST! Never add -EST!"
        },
        {
          type: "order",
          q: "Ordena: 'Ese es el restaurante más popular de la ciudad'",
          words: ["That", "is", "the", "most", "popular", "restaurant", "in", "the", "city."],
          a: "That is the most popular restaurant in the city."
        },
        {
          type: "input",
          q: "Complete: 'Vancouver is ___ most expensive city in Canada.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "Pattern for long adjectives:",
          options: [
            "THE MOST + adjective (no change to adjective)",
            "THE + adjective + -EST",
            "MOST + adjective (no THE)"
          ],
          a: "THE MOST + adjective (no change to adjective)",
          tip: "PATTERN! THE MOST + adjective! Adjective stays the same! 'The most expensive'!"
        }
      ]
    },

    // SET 4: Irregular Superlatives
    {
      id: "m4-u16-set4",
      title: "Set 4: Irregular Superlatives",
      questions: [
        {
          type: "match",
          q: "Match irregular superlatives (MEMORIZE these!):",
          pairs: [
            { left: "good", right: "the best (NOT the goodest!)" },
            { left: "bad", right: "the worst (NOT the baddest!)" },
            { left: "far", right: "the farthest / the furthest" }
          ],
          a: "completed",
          tip: "IRREGULAR! Don't follow rules! MUST MEMORIZE! Good→best, bad→worst!"
        },
        {
          type: "mc",
          q: "'This is a good hotel. That is a good hotel. But this one is ___!' (el mejor)",
          options: ["the best", "the goodest", "the most good"],
          a: "the best",
          tip: "THE BEST! NOT 'goodest' ❌! Good → THE BEST! Most common irregular superlative!"
        },
        {
          type: "input",
          q: "Complete: 'I think summer is ___ best season.' (the)",
          a: "the"
        },
        {
          type: "order",
          q: "Ordena: 'Esta es la mejor opción'",
          words: ["This", "is", "the", "best", "option."],
          a: "This is the best option."
        },
        {
          type: "mc",
          q: "'Winter is bad. Fall is bad. But winter is ___!' (el peor)",
          options: ["the worst", "the baddest", "the most bad"],
          a: "the worst",
          successtip: "✓ THE WORST! Irregular! 👎 Bad → THE WORST (NOT baddest ❌)!"
        },
        {
          type: "input",
          q: "Complete: 'That was ___ worst movie I ever saw!' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "'Far' has two superlative forms:",
          options: [
            "the farthest (distance) / the furthest (degree or distance)",
            "the farest",
            "the most far"
          ],
          a: "the farthest (distance) / the furthest (degree or distance)",
          tip: "TWO FORMS! Farthest = distance. Furthest = distance OR additional. Both work!"
        },
        {
          type: "order",
          q: "Ordena: 'Esta es la ciudad más lejana de aquí'",
          words: ["This", "is", "the", "farthest", "city", "from", "here."],
          a: "This is the farthest city from here."
        },
        {
          type: "input",
          q: "Complete: 'This is ___ best restaurant in Toronto.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "REMEMBER: Good → ___, Bad → ___",
          options: ["the best, the worst", "the goodest, the baddest", "the most good, the most bad"],
          a: "the best, the worst",
          successtip: "✓ MEMORIZED! Critical irregulars! 🏆👎 THE BEST and THE WORST are used constantly!"
        }
      ]
    },

    // SET 5: Canadian Geography Facts
    {
      id: "m4-u16-set5",
      title: "Set 5: Canadian Superlatives (Real Facts)",
      questions: [
        {
          type: "match",
          q: "Match Canadian city to superlative fact:",
          pairs: [
            { left: "Toronto", right: "the largest/biggest city (3M people)" },
            { left: "Vancouver", right: "the most expensive & warmest winters" },
            { left: "Winnipeg", right: "the coldest major city" }
          ],
          a: "completed",
          tip: "Real Canadian facts! Toronto = biggest, Vancouver = priciest/warmest, Winnipeg = coldest!"
        },
        {
          type: "mc",
          q: "'Toronto has 3 million people. Montreal has 2 million. Ottawa has 1 million. Toronto is ___.'",
          options: ["the biggest city", "bigger city", "big city"],
          a: "the biggest city",
          tip: "THE BIGGEST! Three cities = superlative! Toronto = Canada's largest city!"
        },
        {
          type: "input",
          q: "Complete: 'Vancouver is ___ most expensive city in Canada.' (the)",
          a: "the"
        },
        {
          type: "order",
          q: "Ordena: 'Winnipeg es la ciudad más fría de Canadá'",
          words: ["Winnipeg", "is", "the", "coldest", "city", "in", "Canada."],
          a: "Winnipeg is the coldest city in Canada."
        },
        {
          type: "mc",
          q: "Vancouver winters are mild. Toronto winters are cold. Winnipeg winters are very cold. Which is THE coldest?",
          options: ["Winnipeg", "Vancouver", "Toronto"],
          a: "Winnipeg",
          successtip: "✓ WINNIPEG! Prairie cold! ❄️ Winnipeg = coldest major Canadian city (can reach -40°C!)"
        },
        {
          type: "input",
          q: "Complete: 'Quebec City is ___ most French city in Canada.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "The CN Tower in Toronto is 553 meters tall. It is:",
          options: [
            "the tallest free-standing structure in Canada",
            "taller structure",
            "tall structure"
          ],
          a: "the tallest free-standing structure in Canada",
          tip: "THE TALLEST! Famous landmark! 🗼 CN Tower = iconic Toronto symbol!"
        },
        {
          type: "order",
          q: "Ordena: 'Vancouver tiene los inviernos más cálidos'",
          words: ["Vancouver", "has", "the", "warmest", "winters", "in", "Canada."],
          a: "Vancouver has the warmest winters in Canada."
        },
        {
          type: "input",
          q: "Complete: 'Tim Hortons is ___ most popular coffee shop in Canada.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "Yonge Street in Toronto is over 1,800 km long. It is:",
          options: [
            "the longest street in the world (Guinness record - debated)",
            "longer street",
            "long street"
          ],
          a: "the longest street in the world (Guinness record - debated)",
          tip: "THE LONGEST! Toronto fact! 🛣️ Yonge Street = famous for its length!"
        }
      ]
    },

    // SET 6: World Facts (Animals, Geography)
    {
      id: "m4-u16-set6",
      title: "Set 6: World Facts & Extremes",
      questions: [
        {
          type: "match",
          q: "Match world superlative facts:",
          pairs: [
            { left: "Mount Everest", right: "the highest mountain (8,849m)" },
            { left: "The cheetah", right: "the fastest land animal (120 km/h)" },
            { left: "The blue whale", right: "the largest animal on Earth" }
          ],
          a: "completed",
          tip: "World records! General knowledge facts using superlatives!"
        },
        {
          type: "mc",
          q: "'Mount Everest is 8,849 meters high. K2 is 8,611 meters. Everest is ___.'",
          options: ["the highest mountain in the world", "higher mountain", "high mountain"],
          a: "the highest mountain in the world",
          tip: "THE HIGHEST! World record! 🏔️ Everest = tallest mountain on Earth!"
        },
        {
          type: "input",
          q: "Complete: 'The cheetah is ___ fastest animal on land.' (the)",
          a: "the"
        },
        {
          type: "order",
          q: "Ordena: 'El elefante es el animal terrestre más grande'",
          words: ["The", "elephant", "is", "the", "biggest", "land", "animal."],
          a: "The elephant is the biggest land animal."
        },
        {
          type: "mc",
          q: "The blue whale can weigh up to 200 tons. It is:",
          options: [
            "the largest animal in the world",
            "larger animal",
            "large animal"
          ],
          a: "the largest animal in the world",
          successtip: "✓ THE LARGEST! Ocean giant! 🐋 Blue whale = biggest animal ever! Bigger than any dinosaur!"
        },
        {
          type: "input",
          q: "Complete: 'The Sahara is ___ biggest desert in the world.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "Antarctica has temperatures as low as -89°C. It is:",
          options: [
            "the coldest place on Earth",
            "colder place",
            "cold place"
          ],
          a: "the coldest place on Earth",
          tip: "THE COLDEST! Extreme cold! 🥶 Antarctica = coldest continent!"
        },
        {
          type: "order",
          q: "Ordena: 'El Nilo es el río más largo del mundo'",
          words: ["The", "Nile", "is", "the", "longest", "river", "in", "the", "world."],
          a: "The Nile is the longest river in the world."
        },
        {
          type: "input",
          q: "Complete: 'The peregrine falcon is ___ fastest bird.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "The giraffe can be up to 5.5 meters tall. It is:",
          options: [
            "the tallest animal on Earth",
            "taller animal",
            "tall animal"
          ],
          a: "the tallest animal on Earth",
          tip: "THE TALLEST! Long neck! 🦒 Giraffe = tallest living land animal!"
        }
      ]
    },

    // SET 7: Personal Superlatives (Opinions)
    {
      id: "m4-u16-set7",
      title: "Set 7: Personal Opinions & Preferences",
      questions: [
        {
          type: "match",
          q: "Match superlative opinion question:",
          pairs: [
            { left: "What's the best movie?", right: "Opinion about quality" },
            { left: "Who's the tallest in your family?", right: "Personal fact" },
            { left: "What's the most important thing?", right: "Priority/value" }
          ],
          a: "completed",
          tip: "Personal superlatives! Express YOUR opinions and facts about YOUR life!"
        },
        {
          type: "mc",
          q: "'In my opinion, summer is ___.' (el mejor)",
          options: ["the best season", "better season", "good season"],
          a: "the best season",
          tip: "THE BEST! Opinion! ☀️ 'In my opinion' + superlative = expressing preference!"
        },
        {
          type: "input",
          q: "Complete: 'I think Toronto is ___ most interesting city in Canada.' (the)",
          a: "the"
        },
        {
          type: "order",
          q: "Ordena: '¿Cuál es la película más emocionante que has visto?'",
          words: ["What", "is", "the", "most", "exciting", "movie", "you", "know?"],
          a: "What is the most exciting movie you know?"
        },
        {
          type: "mc",
          q: "Personal question: 'Who is ___ in your family?'",
          options: [
            "the tallest / the oldest / the youngest",
            "taller",
            "tall"
          ],
          a: "the tallest / the oldest / the youngest",
          successtip: "✓ PERSONAL FACTS! Family comparisons! 👨‍👩‍👧‍👦 Superlatives for describing your family!"
        },
        {
          type: "input",
          q: "Complete: 'For me, family is ___ most important thing.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "'What's ___ for you - money, health, or love?'",
          options: [
            "the most important",
            "more important",
            "important"
          ],
          a: "the most important",
          tip: "THE MOST IMPORTANT! Values question! 💭 Asking about priorities using superlatives!"
        },
        {
          type: "order",
          q: "Ordena: 'Creo que el otoño es la estación más hermosa'",
          words: ["I", "think", "fall", "is", "the", "most", "beautiful", "season."],
          a: "I think fall is the most beautiful season."
        },
        {
          type: "input",
          q: "Complete: 'This is ___ best coffee I ever drink!' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "YOUR OPINION: 'What's the best city in Canada?'",
          options: [
            "YOUR answer! 'I think [city] is the best because...'",
            "'Yes'",
            "'No'"
          ],
          a: "YOUR answer! 'I think [city] is the best because...'",
          successtip: "✓ EXPRESS YOURSELF! Personal opinion! 🗣️ 'I think X is the best because...' Use superlatives to share YOUR views!"
        }
      ]
    },

    // SET 8: Superlative Idioms & Expressions
    {
      id: "m4-u16-set8",
      title: "Set 8: Superlative Idioms & Slang",
      questions: [
        {
          type: "match",
          q: "Match superlative idiom to meaning:",
          pairs: [
            { left: "top of the line", right: "the best quality (lo mejor)" },
            { left: "cream of the crop", right: "the very best (lo mejor de lo mejor)" },
            { left: "second to none", right: "the best, unbeatable (sin igual)" }
          ],
          a: "completed",
          tip: "Native expressions! Emphatic ways to say 'the best'!"
        },
        {
          type: "mc",
          q: "'This laptop is ___ - it's the best quality!' (idiom = lo mejor)",
          options: ["top of the line", "top of the mountain", "top of the table"],
          a: "top of the line",
          tip: "'TOP OF THE LINE'! Quality idiom! 💎 = the highest quality available! Premium!"
        },
        {
          type: "input",
          q: "Complete: 'These students are the cream of the ___.' (crop = los mejores)",
          a: "crop"
        },
        {
          type: "order",
          q: "Ordena: 'Este restaurante es sin igual - es el mejor'",
          words: ["This", "restaurant", "is", "second", "to", "none", "-", "it's", "the", "best!"],
          a: "This restaurant is second to none - it's the best!"
        },
        {
          type: "mc",
          q: "'Cream of the crop' means:",
          options: [
            "The very best people or things (lo mejor de lo mejor)",
            "About agriculture",
            "Average quality"
          ],
          a: "The very best people or things (lo mejor de lo mejor)",
          successtip: "✓ CREAM OF THE CROP! Elite idiom! 🌾 Originally from farming = best part! Now = best people/things!"
        },
        {
          type: "input",
          q: "Complete: 'This is ___ real deal - it's authentic and the best!' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "'Second to none' means:",
          options: [
            "The best, nobody is better (sin igual)",
            "Second place",
            "Not good"
          ],
          a: "The best, nobody is better (sin igual)",
          tip: "'SECOND TO NONE'! Unbeatable! 🥇 = nobody else is better! Number one!"
        },
        {
          type: "order",
          q: "Ordena: 'Esto es de la mejor calidad - es top'",
          words: ["This", "is", "top", "of", "the", "line", "-", "the", "best", "quality!"],
          a: "This is top of the line - the best quality!"
        },
        {
          type: "input",
          q: "Complete: 'This is hands down ___ best option!' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "'Bar none' (idiom) means:",
          options: [
            "Without exception, absolutely the best",
            "In a bar",
            "Not good"
          ],
          a: "Without exception, absolutely the best",
          tip: "'BAR NONE'! Emphatic! 'The best restaurant, bar none!' = absolutely the best, no exceptions!"
        }
      ]
    },

    // SET 9: Combining Comparatives and Superlatives
    {
      id: "m4-u16-set9",
      title: "Set 9: Using Both Forms Together",
      questions: [
        {
          type: "match",
          q: "Match sentence using BOTH forms:",
          pairs: [
            { left: "Toronto is bigger than Montreal", right: "...but it's the biggest city in Canada" },
            { left: "Vancouver is more expensive than Calgary", right: "...and it's the most expensive in Canada" },
            { left: "Winter is colder than fall", right: "...but it's the coldest season" }
          ],
          a: "completed",
          tip: "Advanced! Use comparative AND superlative in same sentence! Natural flow!"
        },
        {
          type: "mc",
          q: "'Toronto is bigger ___ Montreal, but it's ___ biggest city in Canada.'",
          options: ["than / the", "the / than", "more / most"],
          a: "than / the",
          tip: "THAN vs THE! Comparative uses THAN. Superlative uses THE. Different structures!"
        },
        {
          type: "input",
          q: "Complete: 'Vancouver is more expensive ___ Calgary.' (than)",
          a: "than"
        },
        {
          type: "order",
          q: "Ordena: 'El verano es más caluroso que la primavera, pero es la estación más calurosa'",
          words: ["Summer", "is", "hotter", "than", "spring,", "but", "it's", "the", "hottest", "season."],
          a: "Summer is hotter than spring, but it's the hottest season."
        },
        {
          type: "mc",
          q: "Complete: 'This hotel is better ___ that hotel, but it's ___ best in the city.'",
          options: ["than / the", "the / than", "more / most"],
          a: "than / the",
          successtip: "✓ BOTH FORMS! Natural progression! 🏨 First compare 2 things (than), then identify #1 (the)!"
        },
        {
          type: "input",
          q: "Complete: 'Winnipeg is colder than Toronto, but it's ___ coldest city.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "Pattern: 'A is [comparative] ___ B, but A is ___ [superlative] in [group].'",
          options: ["than / the", "the / than", "more / most"],
          a: "than / the",
          tip: "PATTERN! Than for comparison. The for extreme. Master this for natural speech!"
        },
        {
          type: "order",
          q: "Ordena: 'Este restaurante es mejor que ese, pero es el mejor de la ciudad'",
          words: ["This", "restaurant", "is", "better", "than", "that", "one,", "but", "it's", "the", "best", "in", "the", "city."],
          a: "This restaurant is better than that one, but it's the best in the city."
        },
        {
          type: "input",
          q: "Complete: 'Mount Everest is higher ___ K2, but it's ___ highest mountain.' (than / the)",
          a: "than / the"
        },
        {
          type: "mc",
          q: "Why use both forms together?",
          options: [
            "To show progression: compare 2 things, then identify the ultimate #1",
            "No reason",
            "It's required"
          ],
          a: "To show progression: compare 2 things, then identify the ultimate #1",
          tip: "LOGICAL FLOW! Natural thinking! First narrow down (comparative), then identify winner (superlative)!"
        }
      ]
    },

    // SET 10: Questions with Superlatives
    {
      id: "m4-u16-set10",
      title: "Set 10: Asking Superlative Questions",
      questions: [
        {
          type: "match",
          q: "Match question word to superlative question:",
          pairs: [
            { left: "What", right: "What is the best movie?" },
            { left: "Who", right: "Who is the tallest person?" },
            { left: "Which", right: "Which is the most expensive?" }
          ],
          a: "completed",
          tip: "Question structures! What/Who/Which + superlative = asking for #1!"
        },
        {
          type: "mc",
          q: "Asking about things: '___ is the best restaurant in Toronto?'",
          options: ["What", "Who", "Where"],
          a: "What",
          tip: "'WHAT'! For things! 'What is the best...?' 'What is the most...?'"
        },
        {
          type: "input",
          q: "Complete: '___ is the tallest person in your family?' (Who)",
          a: "Who"
        },
        {
          type: "order",
          q: "Ordena: '¿Cuál es la ciudad más grande de Canadá?'",
          words: ["What", "is", "the", "biggest", "city", "in", "Canada?"],
          a: "What is the biggest city in Canada?"
        },
        {
          type: "mc",
          q: "Choosing between options: '___ hotel is the cheapest - A, B, or C?'",
          options: ["Which", "What", "Who"],
          a: "Which",
          successtip: "✓ WHICH! Choice! 'Which is the...?' = selecting from specific options!"
        },
        {
          type: "input",
          q: "Complete: 'What is ___ most important thing for you?' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "Common superlative questions: 'What's ___ movie you know?'",
          options: [
            "the best / the worst / the most interesting",
            "better",
            "good"
          ],
          a: "the best / the worst / the most interesting",
          tip: "'WHAT'S THE...?' Common question pattern! Very natural conversational structure!"
        },
        {
          type: "order",
          q: "Ordena: '¿Quién es el más joven de tu grupo?'",
          words: ["Who", "is", "the", "youngest", "in", "your", "group?"],
          a: "Who is the youngest in your group?"
        },
        {
          type: "input",
          q: "Complete: '___ is the most expensive city in Canada?' (What/Which)",
          a: "What"
        },
        {
          type: "mc",
          q: "Answering superlative questions: 'What's the best season?' Answer:",
          options: [
            "'I think summer is the best season.'",
            "'Summer.'",
            "'Yes.'"
          ],
          a: "'I think summer is the best season.'",
          successtip: "✓ FULL ANSWER! Opinion! 💭 'I think [X] is the [superlative]' = complete, natural response!"
        }
      ]
    }
  ],
// ===================================================================
// READING SET: Toronto Hotel Guide (Superlatives in Action)
// ===================================================================
readingSet: {
  id: "m4-u16-reading",
  title: "Reading: Toronto Hotel Guide",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Luxury & History (preguntas 1, 2, 3 y 4)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Icons of Toronto**

Planning a trip to Toronto? Here's our guide to the city's hotels!

**THE RITZ-CARLTON TORONTO** - $800/night
This is **THE MOST EXPENSIVE** hotel in Toronto! It has 267 rooms and is 50 stories tall. The service is **THE BEST**—five-star luxury. If you want **THE MOST COMFORTABLE** beds and **THE FINEST** dining, this is your hotel. But be careful—it's **THE PRICIEST** option!

**THE FAIRMONT ROYAL YORK** - $300/night
This is **THE BIGGEST** hotel in Toronto with 1,365 rooms! It opened in 1929, so it's also **THE OLDEST** luxury hotel in the city. Many people think it's **THE MOST BEAUTIFUL** historic hotel in Canada. The location is **THE BEST** for tourists—right next to Union Station!`
    },
    {
      type: "mc",
      q: "¿Cuántos hoteles en total se analizan en esta guía?",
      options: [
        "Cinco hoteles (Ritz, Royal York, Hotel X, Beverly, Budget Motel)",
        "Tres hoteles",
        "Siete hoteles"
      ],
      a: "Cinco hoteles (Ritz, Royal York, Hotel X, Beverly, Budget Motel)",
      successtip: "✓ READING DETAIL!\nHas identificado correctamente la lista completa de opciones. 🏨"
    },
    {
      type: "mc",
      q: "¿Cuál es el hotel más caro (**THE MOST EXPENSIVE**) de la lista?",
      options: [
        "The Ritz-Carlton ($800/noche)",
        "The Royal York",
        "Hotel X"
      ],
      a: "The Ritz-Carlton ($800/noche)",
      successtip: "✓ LUXURY CHOICE!\nEs el hotel con el precio más alto por noche. 💰💎"
    },
    {
      type: "mc",
      q: "¿Cuál es el hotel más grande (**THE BIGGEST**) en cantidad de habitaciones?",
      options: [
        "The Fairmont Royal York (1,365 habitaciones)",
        "The Ritz",
        "Hotel X"
      ],
      a: "The Fairmont Royal York (1,365 habitaciones)",
      successtip: "✓ MASSIVE!\nCon más de mil habitaciones, es el gigante de la ciudad. 🏢"
    },
    {
      type: "mc",
      q: "¿Cuál es el hotel de lujo más antiguo (**THE OLDEST**)?",
      options: [
        "The Royal York (inaugurado en 1929)",
        "The Ritz",
        "Budget Motel"
      ],
      a: "The Royal York (inaugurado en 1929)",
      successtip: "✓ HISTORIC!\nFue construido hace casi un siglo. 🕰️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Modernity & Families (preguntas 5 y 6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Modern Comforts**

**HOTEL X TORONTO** - $250/night
This is **THE NEWEST** luxury hotel—it opened in 2018. It has 404 rooms and is near the lake. It's **THE BEST** for families because it has a huge pool and activities for children. It's more affordable than downtown hotels but **THE FARTHEST** from the city center. It features **THE MOST MODERN** design!`
    },
    {
      type: "mc",
      q: "¿Cuál es el hotel más nuevo (**THE NEWEST**) de la guía?",
      options: [
        "Hotel X Toronto (abierto en 2018)",
        "The Ritz",
        "The Beverly"
      ],
      a: "Hotel X Toronto (abierto en 2018)",
      successtip: "✓ RECENT OPENING!\nEs la construcción más reciente de la lista. 🆕"
    },
    {
      type: "mc",
      q: "¿Cuál es la mejor opción (**THE BEST**) para viajar en familia?",
      options: [
        "Hotel X (tiene piscina y actividades para niños)",
        "The Ritz",
        "Budget Motel"
      ],
      a: "Hotel X (tiene piscina y actividades para niños)",
      successtip: "✓ FAMILY FRIENDLY!\nSus amenidades están diseñadas para niños. 👨‍👩‍👧‍👦🏊‍♂️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Budget & Quality (preguntas 7, 8 y 9)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Budget Options**

**THE BEVERLY HOTEL** - $150/night
This is a boutique hotel with only 18 rooms—**THE SMALLEST** hotel here. It's in Chinatown, which is **THE MOST INTERESTING** neighborhood for food! 

**BUDGET TORONTO MOTEL** - $80/night
This is **THE MOST AFFORDABLE** hotel in Toronto! But be warned—it's also **THE WORST** rated hotel in the city. Reviews say the service is terrible. It's **THE LEAST RECOMMENDED** option!`
    },
    {
      type: "mc",
      q: "¿Cuál es el hotel más pequeño (**THE SMALLEST**) de todos?",
      options: [
        "The Beverly Hotel (solo 18 habitaciones)",
        "The Ritz",
        "The Royal York"
      ],
      a: "The Beverly Hotel (only 18 rooms)",
      successtip: "✓ BOUTIQUE!\nEs una opción pequeña y acogedora en Chinatown. 🏠"
    },
    {
      type: "mc",
      q: "¿Cuál es el hotel más barato (**THE CHEAPEST / THE MOST AFFORDABLE**)?",
      options: [
        "Budget Toronto Motel ($80/noche)",
        "The Beverly",
        "The Ritz"
      ],
      a: "Budget Toronto Motel ($80/night)",
      successtip: "✓ LOWEST PRICE!\nEs el que tiene el precio más bajo, pero ¡cuidado con las reseñas! 💸"
    },
    {
      type: "mc",
      q: "¿Cuál es el hotel con la peor calificación (**THE WORST**) de la ciudad?",
      options: [
        "Budget Toronto Motel (reseñas terribles)",
        "The Ritz",
        "Hotel X"
      ],
      a: "Budget Toronto Motel (terrible reviews)",
      successtip: "✓ THE WORST!\nRecuerda: 'lo barato sale caro'. Poca calidad y mal servicio. 👎"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Summary & Grammar (pregunta 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Conclusion**

In Toronto, you can find **THE BEST** luxury or **THE CHEAPEST** rooms. Your choice depends on what you value most: history, modernity, location, or price.`
    },
    {
      type: "mc",
      q: "¿Cuál es el propósito principal de este artículo?",
      options: [
        "Comparar hoteles usando superlativos para ayudar al viajero según sus prioridades",
        "Decir que todos los hoteles en Toronto son iguales",
        "Promocionar un solo hotel específico"
      ],
      a: "Compare hotels using superlatives to help travelers choose based on priorities",
      successtip: "✓ SUPERLATIVE MASTER! 🎉\n¡Felicidades! Has aprendido a usar superlativos para identificar lo máximo en cada categoría. 🏆"
    }
  ]
},
  // SET 12: Unit Review
  reviewSet: {
    id: "m4-u16-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match superlative rule to example:",
        pairs: [
          { left: "THE + short adj + -EST", right: "the biggest, the coldest" },
          { left: "THE MOST + long adj", right: "the most expensive, the most beautiful" },
          { left: "Irregular", right: "the best, the worst" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "Critical rule: Superlatives ALWAYS use:",
        options: [
          "THE before the adjective",
          "THAN after the adjective",
          "MORE before the adjective"
        ],
        a: "THE before the adjective"
      },
      {
        type: "mc",
        q: "When to use superlatives:",
        options: [
          "Comparing 3+ things to find the most extreme",
          "Comparing 2 things only",
          "Never"
        ],
        a: "Comparing 3+ things to find the most extreme"
      },
      {
        type: "input",
        q: "Complete: Good → ___, Bad → ___",
        a: "the best, the worst"
      },
      {
        type: "input",
        q: "Complete: 'Toronto is ___ biggest city in Canada.' (the)",
        a: "the"
      },
      {
        type: "order",
        q: "Ordena: 'Este es el restaurante más caro de la ciudad'",
        words: ["This", "is", "the", "most", "expensive", "restaurant", "in", "the", "city."],
        a: "This is the most expensive restaurant in the city."
      },
      {
        type: "mc",
        q: "Difference: 'bigger than' vs 'the biggest'",
        options: [
          "'Bigger than' = comparing 2. 'The biggest' = extreme of 3+",
          "They are the same",
          "No difference"
        ],
        a: "'Bigger than' = comparing 2. 'The biggest' = extreme of 3+"
      },
      {
        type: "mc",
        q: "'Top of the line' means:",
        options: [
          "The best quality (lo mejor)",
          "At the top",
          "In a line"
        ],
        a: "The best quality (lo mejor)"
      },
      {
        type: "mc",
        q: "Pattern for long adjectives:",
        options: [
          "THE MOST + adjective",
          "THE + adjective + -EST",
          "adjective + -EST"
        ],
        a: "THE MOST + adjective"
      },
      {
        type: "mc",
        q: "FINAL MASTERY - Complete realistic conversation:\n\nA: 'I need to choose a Canadian city to live in!'\nB: 'Well, Toronto is ___ biggest city in Canada. It has ___ most jobs. But it's also ___ most expensive!'\nA: 'What about Vancouver?'\nB: 'Vancouver is ___ warmest city in winter, but it's also very expensive - maybe ___ second most expensive!'\nA: 'And Montreal?'\nB: 'Montreal is ___ cheapest major city! And it's ___ most European. But winters are cold!'\nA: 'Which is ___ coldest?'\nB: 'Winnipeg is ___ coldest major city. But the cost of living is low!'\nA: 'So which is ___ best?'\nB: 'That depends! What's ___ most important for you - weather, cost, or jobs?'\n\nCorrect superlatives:",
        options: [
          "the / the / the / the / the / the / the / the / the / the / the",
          "a / a / a / a / a / a / a / a / a / a / a",
          "most / most / most / most / most / most / most / most / most / most / most"
        ],
        a: "the / the / the / the / the / the / the / the / the / the / the",
        successtip: "✓ UNIT 16 COMPLETE! Superlatives mastered! 🏆 You can now: form superlatives correctly (THE + -EST / THE MOST), spell them right (biggest, easiest, nicest), use irregulars (the best, the worst), describe extremes (the coldest, the most expensive), use superlative idioms (top of the line, cream of the crop, second to none), ask superlative questions (What's the best?), express strong opinions, combine with comparatives ('bigger than X but the biggest in Y')! From basic 'the biggest' to complex multi-criteria decision-making! You're describing extremes like a native speaker! Real Canadian facts + world knowledge + personal expression! 🇨🇦🌍🗣️"
      }
    ]
  }
};
